/**
 * Fiche produit (product-fiche) : ajout groupé maillot + protection lorsque le bloc
 * « Protection offerte » est présent et que la case de refus n'est pas cochée.
 * La gratuité repose sur une réduction automatique Shopify (BXGY ou équivalent), pas sur ce script.
 */
import { CartUpdatedEvent } from '@theme/events';
import { consolidateSwimsuitGifts, clearSwimsuitGiftDeclined } from '@theme/cart-gift-sync';

function getCartAddUrl() {
  return (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
}

/**
 * Suggestions cochées dans l'encart « Souvent achetés ensemble ».
 * Elles ne sont ajoutées que si l'envoi vient du bouton de l'encart : le bouton
 * principal du formulaire n'ajoute que le maillot et sa protection offerte.
 */
function getBoughtTogetherExtras(form) {
  const requested = form.dataset.btExtras === 'true';
  delete form.dataset.btExtras;
  if (!requested) return [];

  const encart = document.querySelector('bought-together[data-mode="bundle"]');
  return encart?.getExtraItems?.() || [];
}

function initSwimsuitBundleForm(form) {
  const bundleRoot = form.querySelector('.pf__swimsuit-protection[data-pf-protection-gift-variant-id]');
  if (!bundleRoot) return;

  // Ce formulaire prend la main sur l'ajout au panier : l'encart lui délègue son bouton.
  form.dataset.btDelegate = 'true';

  form.addEventListener('submit', async function (event) {
    const optOutInput = form.querySelector('.pf__swimsuit-optout-input');
    const giftDeclined = optOutInput != null && optOutInput.checked;

    const protectionVariantId = bundleRoot.getAttribute('data-pf-protection-gift-variant-id');
    const withGift = giftDeclined === false && protectionVariantId;

    const extraItems = getBoughtTogetherExtras(form);

    // Sans protection offerte ni suggestion cochée, on laisse le comportement natif.
    if (!withGift && extraItems.length === 0) return;

    const variantInput = form.querySelector('input[name="id"]');
    const qtyInput = form.querySelector('input[name="quantity"]');
    if (!variantInput || !qtyInput) return;

    const maillotVariantId = variantInput.value;
    const quantity = Math.max(1, parseInt(qtyInput.value, 10) || 1);

    event.preventDefault();

    const submitBtn = form.querySelector('.pf__btn-cart[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('is-loading');
    }

    const maillotItem = {
      id: maillotVariantId,
      quantity,
    };

    const items = [maillotItem];

    if (withGift) {
      items.push({
        id: protectionVariantId,
        quantity,
        properties: {
          _free_swimsuit_protection: 'true',
        },
      });
    }

    items.push(...extraItems);

    const sections = [];
    const cartDrawer = document.querySelector('cart-drawer');
    const sectionId = cartDrawer?.dataset.sectionId;
    if (sectionId) {
      sections.push(sectionId);
    }

    try {
      const response = await fetch(getCartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items,
          sections: sections.join(','),
        }),
      });

      let data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || "Erreur lors de l'ajout au panier.");
      }

      // La synchro cadeau ne concerne que l'ajout de la protection : si le client
      // l'a refusée et n'ajoute que des suggestions, on n'y touche pas.
      if (sectionId && withGift) {
        // Ajout explicite de la protection : on annule un éventuel refus précédent
        // pour que la synchro ne la retire pas juste après.
        await clearSwimsuitGiftDeclined({ sectionId });

        const syncResult = await consolidateSwimsuitGifts({ sectionId });
        if (syncResult && !syncResult.errors && !syncResult.status) {
          data = syncResult;
        }
      }

      const currentCount = document.querySelector('cart-icon')?.getCurrentItemCount?.();
      let newItemCount = quantity + quantity;
      if (currentCount != null) {
        newItemCount = currentCount + quantity;
      }
      if (data.item_count != null) {
        newItemCount = data.item_count;
      }

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: newItemCount,
            source: 'product-fiche-bundle',
            sections: data.sections,
          },
        ),
      );

      if (cartDrawer) {
        document.dispatchEvent(new CustomEvent('cart:open'));
      }
    } catch (error) {
      console.error(error);
      document.dispatchEvent(
        new CustomEvent('toast:open', {
          detail: { type: 'error', message: error.message || 'Erreur panier' },
        }),
      );
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-loading');
      }
    }
  });
}

document.querySelectorAll('form.pf__form--swimsuit-bundle').forEach(initSwimsuitBundleForm);
